import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Users, Plus, Pencil, Trash2, Shield, User, X, Save, Settings, Eye, EyeOff, Check, Power, Truck, Package, HardHat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Role icons mapping
const ROLE_ICONS = {
  admin: Shield,
  lagerpersonal: Package,
  chauffor: Truck,
  user: User,
};

// Role badge colors
const ROLE_COLORS = {
  admin: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  lagerpersonal: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  chauffor: "bg-green-500/10 text-green-600 border-green-500/30",
  user: "bg-gray-500/10 text-gray-600 border-gray-500/30",
};

export default function UsersPage() {
  const navigate = useNavigate();
  const { user: currentUser, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [permissionsConfig, setPermissionsConfig] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    pin: "",
    display_name: "",
    role: "lagerpersonal"  // Default to lagerpersonal
  });
  const [permissionsData, setPermissionsData] = useState({
    pages: [],
    features: []
  });

  useEffect(() => {
    if (!isAdmin) {
      navigate("/");
      return;
    }
    fetchUsers();
    fetchPermissionsConfig();
  }, [isAdmin, navigate]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (error) {
      toast.error("Kunde inte ladda användare");
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissionsConfig = async () => {
    try {
      const res = await axios.get(`${API}/users/permissions-config`);
      setPermissionsConfig(res.data);
    } catch (error) {
      console.error("Could not fetch permissions config:", error);
    }
  };

  const openCreateDialog = () => {
    setEditingUser(null);
    setFormData({
      username: "",
      pin: "",
      display_name: "",
      role: "lagerpersonal"
    });
    setShowDialog(true);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      pin: "",
      display_name: user.display_name || "",
      role: user.role_template || user.role || "user"
    });
    setShowDialog(true);
  };

  const openPermissionsDialog = (user) => {
    setEditingUser(user);
    setPermissionsData({
      pages: user.effective_permissions?.pages || [],
      features: user.effective_permissions?.features || []
    });
    setShowPermissionsDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        const updateData = {
          display_name: formData.display_name,
          role: formData.role
        };
        if (formData.pin) {
          updateData.pin = formData.pin;
        }
        await axios.put(`${API}/users/${editingUser.id}`, updateData);
        toast.success("Användare uppdaterad");
      } else {
        await axios.post(`${API}/users`, formData);
        toast.success("Användare skapad");
      }
      
      setShowDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ett fel uppstod");
    }
  };

  const handlePermissionsSubmit = async () => {
    try {
      await axios.put(`${API}/users/${editingUser.id}/permissions`, permissionsData);
      toast.success("Behörigheter uppdaterade");
      setShowPermissionsDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte uppdatera behörigheter");
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.put(`${API}/users/${user.id}`, { is_active: !user.is_active });
      toast.success(user.is_active ? "Användare inaktiverad" : "Användare aktiverad");
      fetchUsers();
    } catch (error) {
      toast.error("Kunde inte ändra status");
    }
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("Användare borttagen");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte ta bort användare");
    }
  };

  const togglePage = (page) => {
    setPermissionsData(prev => ({
      ...prev,
      pages: prev.pages.includes(page)
        ? prev.pages.filter(p => p !== page)
        : [...prev.pages, page]
    }));
  };

  const toggleFeature = (feature) => {
    setPermissionsData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  const formatDate = (isoString) => {
    if (!isoString) return "Aldrig";
    return new Date(isoString).toLocaleString("sv-SE", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getRoleDisplayName = (user) => {
    const role = user.role_template || user.role;
    const template = permissionsConfig?.role_templates?.[role];
    if (template) return template.name;
    if (role === "admin") return "Administratör";
    return "Anpassad";
  };

  const getRoleIcon = (user) => {
    const role = user.role_template || user.role;
    return ROLE_ICONS[role] || User;
  };

  const getRoleColor = (user) => {
    const role = user.role_template || user.role;
    return ROLE_COLORS[role] || ROLE_COLORS.user;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-0">
        <Navigation />
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Laddar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />
      
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="h-6 w-6 text-primary" />
              <h1 className="font-heading text-2xl font-bold uppercase tracking-tight" data-testid="users-title">
                Användare & Behörigheter
              </h1>
            </div>
            <Button onClick={openCreateDialog} className="gap-2" data-testid="add-user-btn">
              <Plus className="h-4 w-4" />
              Ny användare
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {/* Role template info cards */}
        {permissionsConfig?.role_templates && (
          <div className="mb-6">
            <h2 className="text-sm font-medium text-muted-foreground mb-3">Tillgängliga roller</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {["admin", "lagerpersonal", "chauffor"].map((roleKey) => {
                const template = permissionsConfig.role_templates[roleKey];
                if (!template) return null;
                const Icon = ROLE_ICONS[roleKey];
                return (
                  <Card key={roleKey} className={`p-3 border ${ROLE_COLORS[roleKey]?.split(' ')[0] || ''}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="h-4 w-4" />
                      <span className="font-medium">{template.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{template.description}</p>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-4" data-testid="users-list">
          {users.map((user) => {
            const RoleIcon = getRoleIcon(user);
            const roleColor = getRoleColor(user);
            
            return (
              <Card key={user.id} className={`p-4 ${user.is_active === false ? 'opacity-60 border-destructive/30' : ''}`} data-testid={`user-${user.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${roleColor.split(' ')[0]}`}>
                      <RoleIcon className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold">{user.display_name || user.username}</h3>
                        <Badge variant="outline" className={roleColor}>
                          {getRoleDisplayName(user)}
                        </Badge>
                        {user.is_active === false && (
                          <Badge variant="destructive">Inaktiv</Badge>
                        )}
                        {user.id === currentUser?.id && (
                          <Badge variant="outline" className="text-green-500 border-green-500">Du</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">@{user.username}</p>
                      <div className="flex items-center gap-4 mt-1">
                        <p className="text-xs text-muted-foreground">
                          Senast inloggad: {formatDate(user.last_login)}
                        </p>
                        {user.role !== 'admin' && user.effective_permissions && (
                          <div className="flex gap-1">
                            <span className="text-xs text-muted-foreground">
                              {user.effective_permissions.pages?.length || 0} sidor
                            </span>
                            <span className="text-xs text-muted-foreground">•</span>
                            <span className="text-xs text-muted-foreground">
                              {user.effective_permissions.features?.length || 0} funktioner
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Permissions button - only for non-admin users */}
                    {user.role !== 'admin' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openPermissionsDialog(user)}
                        className="gap-2"
                        data-testid={`permissions-user-${user.id}`}
                      >
                        <Settings className="h-4 w-4" />
                        <span className="hidden sm:inline">Behörigheter</span>
                      </Button>
                    )}
                    
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => openEditDialog(user)}
                      data-testid={`edit-user-${user.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    
                    {user.id !== currentUser?.id && (
                      <>
                        {/* Toggle active/inactive */}
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleToggleActive(user)}
                          className={user.is_active === false ? "text-green-500" : "text-amber-500"}
                          title={user.is_active === false ? "Aktivera" : "Inaktivera"}
                          data-testid={`toggle-active-${user.id}`}
                        >
                          <Power className="h-4 w-4" />
                        </Button>
                        
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className="text-destructive hover:bg-destructive/10"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Ta bort användare?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Är du säker på att du vill ta bort {user.display_name || user.username}? Detta kan inte ångras.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Avbryt</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleDelete(user.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Ta bort
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </main>

      {/* Create/Edit User Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingUser ? "Redigera användare" : "Skapa ny användare"}
            </DialogTitle>
            <DialogDescription>
              {!editingUser && "Välj en roll för att automatiskt tilldela behörigheter."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="username">Användarnamn</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  disabled={!!editingUser}
                  placeholder="t.ex. erik"
                  data-testid="user-form-username"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="display_name">Visningsnamn</Label>
                <Input
                  id="display_name"
                  value={formData.display_name}
                  onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                  placeholder="t.ex. Erik Andersson"
                  data-testid="user-form-displayname"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="pin">
                  PIN-kod {editingUser && "(lämna tomt för att behålla)"}
                </Label>
                <Input
                  id="pin"
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={formData.pin}
                  onChange={(e) => setFormData({...formData, pin: e.target.value.replace(/\D/g, "").slice(0, 6)})}
                  placeholder="4-6 siffror"
                  required={!editingUser}
                  data-testid="user-form-pin"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="role">Roll</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                  <SelectTrigger data-testid="user-form-role">
                    <SelectValue placeholder="Välj roll" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-amber-500" />
                        Administratör
                      </div>
                    </SelectItem>
                    <SelectItem value="lagerpersonal">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-blue-500" />
                        Lagerpersonal
                      </div>
                    </SelectItem>
                    <SelectItem value="chauffor">
                      <div className="flex items-center gap-2">
                        <Truck className="h-4 w-4 text-green-500" />
                        Chaufför
                      </div>
                    </SelectItem>
                    <SelectItem value="user">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-500" />
                        Anpassad användare
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Role description */}
                {permissionsConfig?.role_templates?.[formData.role] && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {permissionsConfig.role_templates[formData.role].description}
                  </p>
                )}
                {formData.role === "admin" && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Full åtkomst till alla funktioner.
                  </p>
                )}
                {formData.role === "user" && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Grundläggande behörigheter som kan anpassas efter skapande.
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Avbryt
              </Button>
              <Button type="submit" data-testid="user-form-submit">
                {editingUser ? "Spara" : "Skapa"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={showPermissionsDialog} onOpenChange={setShowPermissionsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Behörigheter för {editingUser?.display_name || editingUser?.username}
            </DialogTitle>
            <DialogDescription>
              Anpassa vilka sidor och funktioner användaren ska ha tillgång till.
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="pages" className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="pages">Sidor ({permissionsData.pages.length})</TabsTrigger>
              <TabsTrigger value="features">Funktioner ({permissionsData.features.length})</TabsTrigger>
            </TabsList>
            
            <TabsContent value="pages" className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground">
                Välj vilka sidor användaren kan se i menyn.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {permissionsConfig?.available_pages?.map((page) => (
                  <div
                    key={page}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      permissionsData.pages.includes(page)
                        ? 'bg-primary/10 border-primary'
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => togglePage(page)}
                    data-testid={`page-toggle-${page}`}
                  >
                    <Checkbox
                      checked={permissionsData.pages.includes(page)}
                      onCheckedChange={() => togglePage(page)}
                    />
                    <div>
                      <p className="font-medium">{permissionsConfig.page_labels?.[page] || page}</p>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="features" className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground">
                Välj vilka funktioner användaren kan utföra.
              </p>
              <div className="grid grid-cols-1 gap-3">
                {permissionsConfig?.available_features?.map((feature) => (
                  <div
                    key={feature}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      permissionsData.features.includes(feature)
                        ? 'bg-primary/10 border-primary'
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => toggleFeature(feature)}
                    data-testid={`feature-toggle-${feature}`}
                  >
                    <Checkbox
                      checked={permissionsData.features.includes(feature)}
                      onCheckedChange={() => toggleFeature(feature)}
                    />
                    <div>
                      <p className="font-medium">{permissionsConfig.feature_labels?.[feature] || feature}</p>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
          
          <Separator className="my-4" />
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowPermissionsDialog(false)}>
              Avbryt
            </Button>
            <Button onClick={handlePermissionsSubmit} className="gap-2" data-testid="save-permissions-btn">
              <Save className="h-4 w-4" />
              Spara behörigheter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
